import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Check, CreditCard, AlertCircle, ExternalLink, Loader2, Settings, DollarSign, Zap, Shield } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = '';

const SubscriptionSettings = ({ user, onUserUpdate }) => {
  const { toast } = useToast();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [cashAppHandle, setCashAppHandle] = useState(user?.cash_app || '');
  const [venmoHandle, setVenmoHandle] = useState(user?.venmo || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSavingLinks, setIsSavingLinks] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  
  // Payout method toggle: 'stripe' or 'direct'
  const [payoutMethod, setPayoutMethod] = useState('stripe');
  const [stripeConnected, setStripeConnected] = useState(false);
  const [isConnectingStripe, setIsConnectingStripe] = useState(false);

  useEffect(() => {
    checkSubscriptionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_URL}/api/monetization/plan-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSubscriptionStatus(response.data.subscription_status);
      setSubscriptionActive(response.data.subscription_active);
      setCashAppHandle(response.data.cash_app_handle || '');
      setVenmoHandle(response.data.venmo_handle || '');
      setStripeConnected(!!user?.stripe_account_id);
      
      // Determine current payout method based on user's setup
      if (user?.stripe_account_id) {
        setPayoutMethod('stripe');
      } else if (response.data.cash_app_handle || response.data.venmo_handle) {
        setPayoutMethod('direct');
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const handleStartSubscription = async () => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/api/monetization/subscription/create-checkout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error starting subscription:', error);
      const errorDetail = error.response?.data?.detail;
      
      if (errorDetail === 'already_subscribed') {
        toast({
          title: 'Already Subscribed',
          description: 'You already have an active Pro Plan subscription.',
          variant: 'default'
        });
        checkSubscriptionStatus();
      } else {
        toast({
          title: 'Error',
          description: errorDetail || 'Failed to start subscription. Please try again.',
          variant: 'destructive'
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConnectStripe = async () => {
    setIsConnectingStripe(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/api/monetization/stripe/create-account-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error connecting Stripe:', error);
      toast({
        title: 'Error',
        description: 'Failed to connect Stripe. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsConnectingStripe(false);
    }
  };

  const handleSavePaymentLinks = async () => {
    setIsSavingLinks(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${API_URL}/api/monetization/payment-links`,
        { cash_app_handle: cashAppHandle, venmo_handle: venmoHandle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: 'Saved!',
        description: 'Your payment links have been updated.',
      });
      if (onUserUpdate) {
        // Spread existing user and update cash_app/venmo
        onUserUpdate({ 
          ...user, 
          cash_app: cashAppHandle ? `https://cash.app/$${cashAppHandle.replace('$', '').replace('https://cash.app/', '')}` : '',
          venmo: venmoHandle ? `https://venmo.com/${venmoHandle.replace('@', '').replace('https://venmo.com/', '')}` : ''
        });
      }
    } catch (error) {
      console.error('Error saving payment links:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save payment links. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSavingLinks(false);
    }
  };

  const handleManageBilling = async () => {
    setIsLoadingPortal(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/api/monetization/subscription/create-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast({
        title: 'Error',
        description: 'Failed to open billing portal. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your Pro Plan subscription?')) {
      return;
    }
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/api/monetization/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: 'Subscription Cancelled',
        description: 'Your subscription will remain active until the end of the billing period.',
      });
      checkSubscriptionStatus();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel subscription. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = () => {
    if (subscriptionStatus === 'active') {
      return (
        <div className="flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm font-medium w-fit">
          <Check className="h-4 w-4" />
          Active
        </div>
      );
    }
    if (subscriptionStatus === 'trialing') {
      return (
        <div className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full text-sm font-medium w-fit">
          <AlertCircle className="h-4 w-4" />
          Trial Active
        </div>
      );
    }
    if (subscriptionStatus === 'past_due') {
      return (
        <div className="flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-sm font-medium w-fit">
          <AlertCircle className="h-4 w-4" />
          Payment Issue
        </div>
      );
    }
    if (subscriptionStatus === 'canceled') {
      return (
        <div className="flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-sm font-medium w-fit">
          <AlertCircle className="h-4 w-4" />
          Cancelled
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-r from-[#FFCC00]/10 to-[#FFD700]/10 border-2 border-[#FFCC00]">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-black mb-2">Pro Plan</h3>
            <p className="text-gray-700">$10/month - Keep 100% of your tips</p>
          </div>
          <div className="bg-[#FFCC00] p-3 rounded-lg">
            <CreditCard className="h-6 w-6 text-black" />
          </div>
        </div>

        {!subscriptionActive ? (
          <>
            {subscriptionStatus && subscriptionStatus !== 'pending_checkout' && (
              <div className="mb-4">{getStatusBadge()}</div>
            )}

            {/* Value proposition */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex">
                <Shield className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-2">PRO Subscriber Benefits:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Keep <strong>100%</strong> of all tips</li>
                    <li><strong>PRO Badge</strong> on your profile</li>
                    <li><strong>Priority listing</strong> - appear first to artists</li>
                    <li>Choose how you receive payments (Stripe or direct)</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button
              onClick={handleStartSubscription}
              disabled={isProcessing}
              className="w-full bg-black hover:bg-gray-800 text-white py-6 text-lg"
              size="lg"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Redirecting to checkout...
                </>
              ) : (
                <>
                  Start $10/month Subscription
                  <ExternalLink className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            {/* Status badge */}
            <div className="mb-4">{getStatusBadge()}</div>

            {/* PRO Benefits Summary */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800 font-semibold mb-2">🎉 Your PRO Benefits:</p>
              <ul className="text-sm text-green-800 space-y-1">
                <li>✓ Keep <strong>100%</strong> of all tips</li>
                <li>✓ <strong>PRO badge</strong> on your profile</li>
                <li>✓ <strong>Priority listing</strong> - appear first to artists</li>
                <li>✓ No platform fees</li>
              </ul>
            </div>

            {/* Payout Method Toggle */}
            <div className="mb-6">
              <Label className="text-base font-semibold mb-3 block">How do you want to receive tips?</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPayoutMethod('stripe')}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    payoutMethod === 'stripe'
                      ? 'border-[#635BFF] bg-[#635BFF]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className={`h-5 w-5 ${payoutMethod === 'stripe' ? 'text-[#635BFF]' : 'text-gray-500'}`} />
                    <span className={`font-semibold ${payoutMethod === 'stripe' ? 'text-[#635BFF]' : 'text-gray-700'}`}>
                      Stripe
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">Card, Apple Pay, Google Pay</p>
                  <div className="flex items-center gap-1 mt-2">
                    <Zap className="h-3 w-3 text-green-500" />
                    <span className="text-xs text-green-600 font-medium">Automatic</span>
                  </div>
                </button>

                <button
                  onClick={() => setPayoutMethod('direct')}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    payoutMethod === 'direct'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className={`h-5 w-5 ${payoutMethod === 'direct' ? 'text-green-600' : 'text-gray-500'}`} />
                    <span className={`font-semibold ${payoutMethod === 'direct' ? 'text-green-600' : 'text-gray-700'}`}>
                      Direct
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">Cash App, Venmo</p>
                  <div className="flex items-center gap-1 mt-2">
                    <AlertCircle className="h-3 w-3 text-yellow-500" />
                    <span className="text-xs text-yellow-600 font-medium">Manual verify</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Stripe Setup Section */}
            {payoutMethod === 'stripe' && (
              <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">Stripe Connect</h4>
                    <p className="text-sm text-gray-500">Receive payments instantly to your bank</p>
                  </div>
                  {stripeConnected ? (
                    <div className="flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm font-medium">
                      <Check className="h-4 w-4" />
                      Connected
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-sm font-medium">
                      <AlertCircle className="h-4 w-4" />
                      Not Set Up
                    </div>
                  )}
                </div>

                {!stripeConnected && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      Connect your Stripe account to receive automatic payments from artists. No manual verification needed!
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleConnectStripe}
                  disabled={isConnectingStripe}
                  className={`w-full ${stripeConnected ? 'bg-gray-600 hover:bg-gray-700' : 'bg-[#635BFF] hover:bg-[#635BFF]/90'} text-white`}
                >
                  {isConnectingStripe ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : stripeConnected ? (
                    <>
                      <Settings className="mr-2 h-4 w-4" />
                      Manage Stripe Account
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Connect Stripe Account
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Direct Payment Setup Section */}
            {payoutMethod === 'direct' && (
              <div className="space-y-4 mb-6 p-4 bg-gray-50 rounded-xl">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Direct Payment Links</h4>
                  <p className="text-sm text-gray-500">Artists will send tips directly to your Cash App or Venmo</p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Direct payments require you to manually verify each tip before the artist&apos;s track gets priority.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="cash_app" className="text-sm font-semibold mb-2 block">
                      Cash App Username
                    </Label>
                    <Input
                      id="cash_app"
                      type="text"
                      placeholder="$YourUsername"
                      value={cashAppHandle}
                      onChange={(e) => setCashAppHandle(e.target.value)}
                      className="h-11"
                    />
                  </div>

                  <div>
                    <Label htmlFor="venmo" className="text-sm font-semibold mb-2 block">
                      Venmo Username
                    </Label>
                    <Input
                      id="venmo"
                      type="text"
                      placeholder="@YourUsername"
                      value={venmoHandle}
                      onChange={(e) => setVenmoHandle(e.target.value)}
                      className="h-11"
                    />
                  </div>

                  <Button
                    onClick={handleSavePaymentLinks}
                    disabled={isSavingLinks || (!cashAppHandle && !venmoHandle)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    {isSavingLinks ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Save Payment Links
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Manage Billing & Cancel */}
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <Button
                onClick={handleManageBilling}
                disabled={isLoadingPortal}
                variant="outline"
                className="w-full border-2"
              >
                {isLoadingPortal ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Opening...
                  </>
                ) : (
                  <>
                    <Settings className="mr-2 h-4 w-4" />
                    Manage Subscription Billing
                  </>
                )}
              </Button>

              <Button
                onClick={handleCancelSubscription}
                disabled={isProcessing}
                variant="outline"
                className="w-full border-2 border-red-500 text-red-500 hover:bg-red-50"
              >
                {isProcessing ? 'Processing...' : 'Cancel Subscription'}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default SubscriptionSettings;
